/**
 * @fileoverview Hook agregador da página Multi unidades.
 *
 * Encapsula a fonte de dados em um único ponto: gera massa falsa
 * (enquanto dev está sem dados reais) e calcula TODAS as métricas via
 * funções puras de metrics.js. Em produção, basta trocar `useDataset`
 * por um hook que chama o backend — os componentes da UI não mudam.
 *
 * Retorna { kpis, performance, charts, smsCounters, ... } já filtrados
 * pelos `filters` recebidos.
 */

import { useMemo, useRef, useState, useCallback, useEffect } from "react";
import api from "../../../services/api";
import { generateDataset, generateMessagePreview } from "./seed";
import {
  computeKpis,
  computePerformanceBySmartCenter,
  computeHistorical,
  computeSmsCounters,
  filterConversations,
  filterConversationsForHistorical,
  getOpenCasesForUnit,
} from "./metrics";

// Dataset singleton — gera UMA vez (por seed/companies) pra não pagar
// geração toda renderização. Quando companies reais chegam do backend,
// regenera com elas como input pra que os SCs tenham nome/id das empresas.
let __cachedDataset = null;
let __cachedCompaniesKey = null;

const buildDataset = (realCompanies) => {
  const t0 = Date.now();
  const ds = generateDataset({
    seed: 0xCafeBabe,
    now: new Date(),
    realCompanies,
  });
  if (typeof window !== "undefined") {
    window.__multiUnidadesDataset = ds;
    // eslint-disable-next-line no-console
    console.info(
      `[MultiUnidades] dataset gerado em ${Date.now() - t0}ms (${realCompanies?.length || 0} companies reais)`,
      {
        companies: ds.companies.length,
        smartCenters: ds.smartCenters.length,
        agents: ds.agents.length,
        customers: ds.customers.length,
        conversations: ds.conversations.length,
        messages: ds.messages.length,
        usesRealCompanies: (realCompanies?.length || 0) > 0,
      }
    );
  }
  return ds;
};

const ensureDataset = (realCompanies) => {
  const key = (realCompanies || []).map((c) => c.id).sort().join(",");
  if (!__cachedDataset || __cachedCompaniesKey !== key) {
    __cachedDataset = buildDataset(realCompanies);
    __cachedCompaniesKey = key;
  }
  return __cachedDataset;
};

export const useMultiUnidadesData = (filters) => {
  // ── Companies reais via API ──────────────────────────────────────
  // Busca companies do backend pra que cada Smart Center na UI
  // corresponda a uma empresa REAL do sistema (consistência com a
  // listagem em /companies). Operação (tempos/contagens) continua
  // simulada enquanto o backend não expõe métricas — UI marca isso
  // visualmente via `companiesSource`.
  const [realCompanies, setRealCompanies] = useState(() => {
    // Reaproveita cache global da página se já populado
    if (typeof window !== "undefined" && window.__multiUnidadesRealCompanies) {
      return window.__multiUnidadesRealCompanies;
    }
    return [];
  });
  const [companiesLoading, setCompaniesLoading] = useState(realCompanies.length === 0);
  const [companiesError, setCompaniesError] = useState(null);

  const fetchCompanies = useCallback(async () => {
    setCompaniesLoading(true);
    setCompaniesError(null);
    try {
      // /companies/list é restrito a super; /companies/:id existe pra todos.
      // Tentamos /companies/list primeiro; se 403, caímos no /companies
      // (paginado). Em prod ambos retornam shape consistente.
      let list = [];
      try {
        const { data } = await api.get("/companies/list");
        list = Array.isArray(data) ? data : [];
      } catch (_) {
        try {
          const { data } = await api.get("/companies", { params: { pageNumber: 1 } });
          list = (data?.companies || data || []).map((c) => ({
            id: c.id, name: c.name, groupName: c.groupName,
          }));
        } catch (e2) {
          throw e2;
        }
      }
      // Normaliza shape
      const normalized = list.map((c) => ({
        id: c.id,
        name: c.name,
        groupName: c.groupName || "",
      }));
      setRealCompanies(normalized);
      if (typeof window !== "undefined") {
        window.__multiUnidadesRealCompanies = normalized;
      }
    } catch (err) {
      setCompaniesError(err?.message || "Não foi possível carregar companies reais.");
      setRealCompanies([]); // segue com fallback do generator
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (realCompanies.length === 0 && !companiesError) fetchCompanies();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mantém o dataset estável entre renders. Re-gera quando companies
  // reais mudam.
  const datasetRef = useRef(null);
  const datasetKey = realCompanies.map((c) => c.id).sort().join(",");
  if (!datasetRef.current || datasetRef.current.__key !== datasetKey) {
    const ds = ensureDataset(realCompanies);
    datasetRef.current = { ...ds, __key: datasetKey };
  }
  const dataset = datasetRef.current;
  const usesRealCompanies = realCompanies.length > 0;

  // Indices úteis (memoizados)
  const indices = useMemo(() => {
    const smartCentersById = new Map(dataset.smartCenters.map((sc) => [sc.id, sc]));
    const agentsById = new Map(dataset.agents.map((a) => [a.id, a]));
    const customersById = new Map(dataset.customers.map((c) => [c.id, c]));
    return { smartCentersById, agentsById, customersById };
  }, [dataset]);

  // Conversas filtradas por (subsidiária, smart center, período)
  const filteredConversations = useMemo(
    () => filterConversations({ conversations: dataset.conversations, filters, now: dataset.now }),
    [dataset, filters.subsidiary, filters.smartCenterId, filters.period, filters.dateRange] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // KPIs (computados sobre filtered)
  const kpis = useMemo(
    () => computeKpis({
      conversations: filteredConversations,
      now: dataset.now,
      smartCentersById: indices.smartCentersById,
    }),
    [filteredConversations, dataset.now, indices.smartCentersById]
  );

  // Performance por SC (sempre sobre o subset filtrado por subsidiária +
  // período; status filtra DEPOIS na UI)
  const performance = useMemo(() => {
    // SCs candidatos: respeitam filtro de subsidiária
    const candidateScs = dataset.smartCenters.filter((sc) => {
      if (filters.subsidiary && filters.subsidiary !== "all" && sc.subsidiaryId !== filters.subsidiary) return false;
      return true;
    });
    let rows = computePerformanceBySmartCenter({
      conversations: filteredConversations,
      smartCenters: candidateScs,
      now: dataset.now,
    });
    if (filters.status && filters.status !== "all") {
      rows = rows.filter((r) => r.status === filters.status);
    }
    if (filters.search) {
      const s = filters.search.toLowerCase();
      rows = rows.filter((r) => r.name.toLowerCase().includes(s) || (r.ascCode || "").includes(filters.search));
    }
    return rows;
  }, [
    dataset,
    filteredConversations,
    filters.subsidiary,
    filters.status,
    filters.search,
  ]);

  // Gráficos históricos — usa filteredForHistorical (sem corte de período)
  const charts = useMemo(() => {
    const subset = filterConversationsForHistorical({ conversations: dataset.conversations, filters });
    return computeHistorical({ conversations: subset, now: dataset.now });
  }, [dataset, filters.subsidiary, filters.smartCenterId]); // eslint-disable-line react-hooks/exhaustive-deps

  // SMS counters (sobre filtered)
  const sms = useMemo(() => {
    const counters = computeSmsCounters({ conversations: filteredConversations, now: dataset.now });
    return {
      counters,
      partners: dataset.smsBase.partners,
      alertLevels: dataset.smsBase.alertLevels,
    };
  }, [filteredConversations, dataset]);

  // Subsidiárias e Smart Centers pra preencher selects
  const optionLists = useMemo(() => {
    const subsidiariesAll = dataset.subsidiaries.map((s) => ({ value: s.value || s.id, label: s.name }));
    const subsidiaries = [{ value: "all", label: "Todas" }, ...subsidiariesAll];
    let scsForFilter = dataset.smartCenters;
    if (filters.subsidiary && filters.subsidiary !== "all") {
      scsForFilter = scsForFilter.filter((sc) => sc.subsidiaryId === filters.subsidiary);
    }
    const smartCenterOptions = [
      { value: "all", label: "Todas as unidades" },
      ...scsForFilter.map((sc) => ({ value: sc.id, label: sc.name })),
    ];
    return { subsidiaries, smartCenterOptions };
  }, [dataset, filters.subsidiary]);

  // Casos abertos para uma unidade (drill-in)
  const getCasesForUnit = useCallback(
    (smartCenterId) => {
      const cases = getOpenCasesForUnit({
        conversations: filteredConversations,
        smartCenterId,
        now: dataset.now,
        limit: 50,
      });
      // Hidrata com nome do agente + cliente + preview da última msg
      return cases.map((kase) => {
        const conv = dataset.conversations.find((c) => c.id === kase.id);
        const agentName = indices.agentsById.get(kase.agentId)?.name || "—";
        const customerName = indices.customersById.get(kase.customerId)?.name || "—";
        return {
          ...kase,
          agent: agentName,
          client: customerName,
          lastMessage: conv ? generateMessagePreview(conv, agentName) : "—",
        };
      });
    },
    [filteredConversations, dataset, indices]
  );

  // Mini-cards de uma unidade (usados na drill-in)
  const getUnitSummary = useCallback(
    (smartCenterId) => {
      const sc = indices.smartCentersById.get(smartCenterId);
      if (!sc) return null;
      const perfRow = performance.find((r) => r.id === smartCenterId)
        || computePerformanceBySmartCenter({
          conversations: filteredConversations,
          smartCenters: [sc],
          now: dataset.now,
        })[0];
      return { sc, perf: perfRow };
    },
    [indices, performance, filteredConversations, dataset.now]
  );

  // Refresh: re-busca companies reais + regenera dataset
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => {
    __cachedDataset = null;
    __cachedCompaniesKey = null;
    datasetRef.current = null;
    setRefreshKey((k) => k + 1);
    // Re-fetch companies em paralelo
    fetchCompanies();
  }, [fetchCompanies]);

  // Total de conversas no dataset bruto (pra exibir contadores)
  const datasetMeta = useMemo(() => ({
    totalConversations: dataset.conversations.length,
    totalMessages: dataset.messages.length,
    totalCompanies: dataset.companies.length,
    totalAgents: dataset.agents.length,
    totalCustomers: dataset.customers.length,
    totalSmartCenters: dataset.smartCenters.length,
    now: dataset.now,
  }), [dataset]);

  return {
    kpis,
    performance,
    charts,
    sms,
    optionLists,
    getCasesForUnit,
    getUnitSummary,
    refresh,
    refreshKey,
    datasetMeta,
    // Metadados sobre a fonte dos dados (pra UI marcar visualmente)
    dataSource: {
      usesRealCompanies,
      realCompaniesCount: realCompanies.length,
      companiesLoading,
      companiesError,
      // Operação (conversations/messages) ainda é simulada mesmo
      // quando companies são reais — backend não tem endpoint próprio
      // de métricas do tracking panel
      operationsSimulated: true,
    },
    // exposto pra validate.js e pra debug:
    _filteredConversations: filteredConversations,
    _dataset: dataset,
  };
};
